import React, { useEffect, useState, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Row, Tooltip } from 'antd';
import classNames from 'classnames';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';

import { constants } from '@amplement/backend-connector';

import AvatarList from 'components/Shared/Call/AvatarList';
import Button from 'components/Shared/Common/Button';
import Link from 'components/Shared/Common/Link';
import { PopConfirmDeletion } from 'components/Shared/Common/PopConfirm';
import { container as CallTransferButton } from 'components/Shared/Call/Buttons/CallTransferDropDown';
import { StyledExpandableSectionListItemLink, StyledPulse, StyledExpandableSectionListItemActions, StyledTransfertButton } from 'components/Shared/style';

import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';

import { getRoomFeatures } from 'selectors/room';
import { getUrl } from 'utils/url';


const AVATAR_LIST_LENGTH = 5;

const PulseCircle = () => (
    <StyledPulse>
        <div className="pulse-circle" />
    </StyledPulse>
);

const MemberAvatars = ({ members, filterStatus }) => {
    const [filteredMember, setFilteredMember] = useState([]);
    useEffect(() => {
        setFilteredMember(members.filter((member) => member.status === filterStatus));
    }, [members, filterStatus]);

    return (
        <div
            className="visible"
            style={{
                marginRight: '0px'
            }}
        >
            {filteredMember && (
                <AvatarList
                    members={filteredMember}
                    size="sm"
                    maxCount={AVATAR_LIST_LENGTH}
                    hasMore
                />
            )}
        </div>
    );
}

const LiveRoom = ({
    name = '',
    id,
    members = [],
    roomUrl,
    target,
    features
}) => { 

    const hasTransfer = features?.transferToUser || features?.transferToPhonenumber || features?.transferToSipUser || features?.transferToRoom;

    return (
        <StyledExpandableSectionListItemLink
            className={classNames('item', { active: true, 'transfer-active': hasTransfer })}
        >
            <Link href={roomUrl} target={target}>
                <div className="wrapper-content">
                    <Row justify="start" align="middle" wrap={false}>
                        <Button
                            color="lighten"
                            isTransparent
                            className="s-btn-video"
                            size="xs"
                            hasHover={false}
                            iconName="Video"
                        />
                        <div className="name">
                            <div className="content">
                                <Tooltip placement="top" title={name}>
                                    {name}
                                </Tooltip>
                            </div>
                            <PulseCircle />
                        </div>
                    </Row>
                    <Row wrap={false} justify="start" align="middle" className="s-second-row">
                        <div className="actions">
                            <MemberAvatars
                                members={members}
                                filterStatus={constants.rooms.USER_IN_ROOM_STATUS.JOINED}
                            />
                        </div>
                    </Row>
                </div>
            </Link>
            <StyledTransfertButton>
                <CallTransferButton
                    _room={id}
                    isLeftMenu
                    transferToUser={features?.transferToUser}
                    transferToPhonenumber={features?.transferToPhonenumber}
                    transferToSipUser={!!features?.transferToSipUser}
                    transferToRoom={!!features?.transferToRoom}
                />
            </StyledTransfertButton>
        </StyledExpandableSectionListItemLink>
    )
};

const RoomItem = (props) => {
    const {
        name,
        id,
        intl,
        isLive = false,
        members = [],
        _feed,
        onDelete,
        onLeave,
        isInitiator
    } = props;

    const roomUrl = getUrl('room', { _room: id });
    const features = useSelector(state => getRoomFeatures(state, id));
    const target = _feed ? `roomByFeed-${_feed}` : `room-${id}`;

    useWhyDidYouUpdate('components:colleft:rooms:item', props);

    const handleDelete = useCallback(() => onDelete(id), [onDelete, id]);
    const handleLeave = useCallback(() => onLeave(id), [onLeave, id]);

    if (isLive) {
        return (
            <LiveRoom
                {...props}
                roomUrl={roomUrl}
                features={features}
                target={target}
                name={name}
                data-testid="LiveRoom"
                members={members}
            />
        );
    }

    return (
        <StyledExpandableSectionListItemLink className="item">
            <Link href={roomUrl} target={target}>
                <div className="wrapper-content">
                    <Row justify="start" align="middle" wrap={false}>
                        <Button
                            color="lighten"
                            isTransparent
                            className="s-btn-video"
                            size="xs"
                            hasHover={false}
                            iconName="Video"
                        />
                        <div className="name">
                            <div className="content">
                                {name}
                            </div>
                        </div>
                    </Row>
                    <Row wrap={false} justify="start" align="middle" className="s-second-row">
                        <div className="actions">
                            <MemberAvatars
                                members={members}
                                filterStatus={constants.rooms.USER_IN_ROOM_STATUS.JOINED}
                            />
                        </div>
                    </Row>
                </div>
            </Link>
            <StyledExpandableSectionListItemActions className="s-list-actions">
                <div className="hovered">
                    <PopConfirmDeletion
                        title={intl.formatMessage({
                            id: isInitiator ? 'room.actions.confirmDelete' : 'room.actions.confirmLeaving'
                        }, { name })}
                        onConfirm={isInitiator ? handleDelete: handleLeave}
                        buttonIconName={isInitiator ? 'XCircle' : 'MinusCircle'}
                        buttonTestId={isInitiator ? 'deleteButton' : 'leaveButton'}
                    />
                </div>
            </StyledExpandableSectionListItemActions>
        </StyledExpandableSectionListItemLink>
    );
};

RoomItem.propTypes = {
    intl: PropTypes.any.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    _feed: PropTypes.string,
    isLive: PropTypes.bool,
    isInitiator: PropTypes.bool,
    members: PropTypes.array,
    onLeave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export default memo(injectIntl(RoomItem), isEqual);
